/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {useContext, useEffect, useMemo, useRef} from 'react'
import {GoogleTagManagerContext} from '../google-tag-manager/contexts'
import {BRAND_LABEL} from '../../brand'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useLocation} from 'react-router-dom'
import useUtils from '../../custom-hooks/use-utils-helper'

const googleTagManagerBodyScript = (id) =>
    `(function(w, d, s, l, i) {
            w[l] = w[l] || []
            w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});
            var f = d.getElementsByTagName(s)[0],
            j = d.createElement(s),
            dl = l != 'dataLayer' ? '&l=' + l : ''

            j.async = true;
            j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl

    f.parentNode.insertBefore(j, f)
    })(window, document, 'script', 'dataLayer', '${id}')`

const googleTagManagerHeadNoScript = (id) =>
    `<iframe src="https://www.googletagmanager.com/ns.html?id=${id}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`

export const useGoogleTagManager = () => {
    const gtma = useContext(GoogleTagManagerContext)
    const config = gtma._config
    const id = config.id
    const {locale} = useMultiSite()
    const {data: customer} = useCurrentCustomer()

    const location = useLocation()
    const {isProductDetail} = useUtils().currentPage(location.pathname)
    const currentPathname = useRef(null)

    /**
     * Appends GTM head noscript
     *
     * @returns {void}
     */
    const setupBodyNoScript = () => {
        const script = document.createElement('noscript')
        script.setAttribute('data-testid', 'gtm-body-script')
        // script.setAttribute('nonce', 'bXktc2VjdXJlLW5vbmNl')
        script.innerHTML = googleTagManagerHeadNoScript(id)
        document.body.appendChild(script)
    }

    /**
     * Appends GTM body script
     *
     * @returns {void}
     */
    const setupHeadScript = () => {
        const script = document.createElement('script')
        script.setAttribute('data-testid', 'gtm-head-script')
        // script.setAttribute('nonce', 'bXktc2VjdXJlLW5vbmNl')
        script.innerHTML = googleTagManagerBodyScript(id)
        document.head.appendChild(script)
    }

    /**
     * Appends Evergage Beacon script
     * The Marketing Cloud Personalization module of the Salesforce Interactions SDK
     * uses a JavaScript beacon to monitor and track the online behavior of an
     * organization’s web users.
     *
     * @returns {void}
     */
    const setupEvergageBeaconScript = () => {
        setTimeout(() => {
            const script = document.createElement('script')
            script.setAttribute('data-testid', 'evergage-beacon-script')
            script.async = true
            script.src = '//cdn.evgnet.com/beacon/delonghi/delonghi_prod/scripts/evergage.min.js'
            document.body.appendChild(script)
        }, 850)
    }

    /**
     * Checks if window.dataLayer is ready and then initializes scripts
     *
     * @returns {void}
     */

    useEffect(() => {
        if (!gtma._isReady && id) {
            queueMicrotask(() => {
                setupHeadScript()
                setupBodyNoScript()
                setupEvergageBeaconScript()
            })
            gtma.initialize()
        }
    }, [])

    /**
     * Add layer
     *
     * @param {object} data - Arguments
     * @param {function} builder - Dedicated builder function for the arguments
     * @returns {object} - sent object
     */
    const addLayer = async (args, builder) =>
        gtma._isReady && id && (await gtma.addLayer(builder ? builder({...args}) : args))

    const addLayerAtPositionZero = async (args, builder) => {
        if (gtma._isReady && id) {
            const data = builder ? await builder({...args}) : args

            window.dataLayer = window.dataLayer || []

            if (window.dataLayer?.[0]?.event === 'realPageview') {
                window.dataLayer[0] = data
            } else {
                window.dataLayer.unshift(data)
            }

            if (currentPathname.current !== data.virtualUrl) {
                const sendEvent =
                    (isProductDetail && location.search.includes('pid')) || !isProductDetail

                if (sendEvent) {
                    window.dispatchEvent(new Event('locationchange'))
                    currentPathname.current = data.virtualUrl
                }
            }

            return data
        }
    }

    const self = useMemo(
        () => ({
            ...config,
            addLayer: addLayer.bind(this, {
                locale,
                isGuest: customer.isGuest,
                customerId: customer.customerId,
                isRegistered: customer.isRegistered,
                brand: BRAND_LABEL,
            }),
            addLayerAtPositionZero: addLayerAtPositionZero.bind(this, {
                locale,
                isGuest: customer.isGuest,
                customerId: customer.customerId,
                isRegistered: customer.isRegistered,
                brand: BRAND_LABEL,
            }),
        }),
        [customer],
    )

    return self
}
